/** Client reschedule: pick a new time; on failure the original booking is untouched (server is atomic). */
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Banner } from '@astryxdesign/core/Banner';
import { BottomSheet } from '@astryxdesign/core/BottomSheet';
import { Button } from '@astryxdesign/core/Button';
import { Heading } from '@astryxdesign/core/Heading';
import { VStack } from '@astryxdesign/core/Layout';
import { Text } from '@astryxdesign/core/Text';
import { useToast } from '@astryxdesign/core/Toast';
import type { BookingView, Slot } from '@shared/contracts.ts';
import { ApiError } from '@shared/errors.ts';
import { useTenant } from '@/app/tenant-context.ts';
import { errorText, publicApi } from '@/lib/api.ts';
import { longDateTime } from '@/lib/format.ts';
import { bookingDays } from '@/features/booking/days.ts';
import { SlotPicker } from '@/features/booking/SlotPicker.tsx';

export function RescheduleSheet({
  isOpen,
  onOpenChange,
  booking,
  token,
  onDone,
}: {
  isOpen: boolean;
  onOpenChange: (o: boolean) => void;
  booking: BookingView;
  token: string;
  onDone: (startsAt: string) => void;
}) {
  const { slug, tenant } = useTenant();
  const qc = useQueryClient();
  const showToast = useToast();
  const days = useMemo(() => bookingDays(tenant), [tenant]);
  const [day, setDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [error, setError] = useState<string | null>(null);
  // One key per chosen target time: retries of the same choice are idempotent.
  const [opKey, setOpKey] = useState(() => crypto.randomUUID());

  const submit = async () => {
    if (!slot) return;
    setError(null);
    try {
      await publicApi.reschedule(slug, token, slot.starts_at, opKey);
      onDone(slot.starts_at);
      showToast({ body: `Запись перенесена на ${longDateTime(slot.starts_at, tenant.timezone)}` });
      await qc.invalidateQueries({ queryKey: ['booking', slug, booking.id] });
      void qc.invalidateQueries({ queryKey: ['slots', slug] });
      onOpenChange(false);
      setSlot(null);
      setOpKey(crypto.randomUUID());
    } catch (e) {
      setError(errorText(e) + (e instanceof ApiError && e.code === 'slot_taken' ? ' Ваша прежняя запись сохранена.' : ''));
      if (e instanceof ApiError && e.code === 'slot_taken') {
        setSlot(null);
        void qc.invalidateQueries({ queryKey: ['slots', slug] });
      }
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onOpenChange={onOpenChange} label="Перенос записи" height="tall" purpose="form">
      <VStack gap={4}>
        <VStack gap={1}>
          <Heading level={2}>Перенести запись</Heading>
          <Text type="supporting">Сейчас: {longDateTime(booking.starts_at, tenant.timezone)}</Text>
        </VStack>
        {error && <Banner status="warning" title={error} collapsible={false} />}
        <SlotPicker
          days={days}
          day={day}
          onDayChange={(d) => (setDay(d), setSlot(null))}
          selected={slot?.starts_at ?? null}
          onSelect={(s) => (setSlot(s), setOpKey(crypto.randomUUID()))}
          timezone={tenant.timezone}
          queryKey={['slots', slug, booking.service_id]}
          fetchDay={(d, n) => publicApi.slots(slug, booking.service_id, d, n)}
        />
        <Button
          label={slot ? `Перенести на ${longDateTime(slot.starts_at, tenant.timezone)}` : 'Выберите новое время'}
          variant="primary"
          size="lg"
          width="100%"
          isDisabled={!slot}
          clickAction={submit}
        />
      </VStack>
    </BottomSheet>
  );
}
